import { useCallback, useMemo } from "react";
import { useFetchClinicQuery } from "@hooks/queries/clinic/useFetchClinicQuery";
import { useFetchDietitianAccountQuery } from "@hooks/queries/settings/useFetchDietitianAccountQuery";
import { WhitelabelDto } from "@client/resources/ClinicResource";

export const useClinicWhitelabel = () => {
  const { clinic: clinicData } = useFetchClinicQuery();
  const { account } = useFetchDietitianAccountQuery();

  const whitelabelFromApi = clinicData?.whitelabel ?? null;

  const mappedWhitelabel = useMemo<WhitelabelDto | null>(() => {
    const accountClinic = account?.clinic;
    if (!accountClinic) return null;

    return mapAccountWhitelabelToDto(accountClinic);
  }, [account]);

  const whitelabel = useMemo<WhitelabelDto | null>(() => {
    if (!whitelabelFromApi && !mappedWhitelabel) {
      return null;
    }

    if (!whitelabelFromApi) {
      return mappedWhitelabel;
    }

    if (!mappedWhitelabel) {
      return whitelabelFromApi;
    }

    return {
      motherClinic:
        whitelabelFromApi.motherClinic ?? mappedWhitelabel.motherClinic,
      partnerClinic:
        whitelabelFromApi.partnerClinic ?? mappedWhitelabel.partnerClinic,
    };
  }, [whitelabelFromApi, mappedWhitelabel]);

  const isMotherClinic = useMemo(() => {
    return (
      whitelabel?.motherClinic !== null &&
      whitelabel?.motherClinic !== undefined
    );
  }, [whitelabel]);

  const isPartnerClinic = useMemo(
    () => whitelabel?.partnerClinic === true,
    [whitelabel],
  );

  const getMotherClinicName = useCallback(
    (fallback: string) => whitelabel?.motherClinic?.name ?? fallback,
    [whitelabel],
  );

  return {
    whitelabel,
    isMotherClinic,
    isPartnerClinic,
    getMotherClinicName,
  };
};

const mapAccountWhitelabelToDto = (
  accountClinic: NonNullable<
    NonNullable<
      ReturnType<typeof useFetchDietitianAccountQuery>["account"]
    >["clinic"]
  >,
): WhitelabelDto => {
  return {
    motherClinic: accountClinic.whitelabel.motherClinic,
    partnerClinic: accountClinic.whitelabel.partnerClinic ?? null,
  };
};
